


import React, {
    useEffect,
    useState
} from "react";

import {
    useNavigate,
    useParams
} from "react-router-dom";

import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";


function AddCategory() {

    const navigate = useNavigate();

    const { id } = useParams();


    /*
    |--------------------------------------------------------------------------
    | Edit Mode
    |--------------------------------------------------------------------------
    */

    const isEditMode = Boolean(id);


    /*
    |--------------------------------------------------------------------------
    | Form Data
    |--------------------------------------------------------------------------
    */

    const [formData, setFormData] = useState({

        paper: "",

        price: "",

        duration: "",

        link: "",

        detail1: "",

        detail2: "",

        detail3: "",

        detail4: "",

        negative_marks: "0",

        marks: "0",

        show_on_home: "No",

        status: "Enable"

    });


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    const [loading, setLoading] =
        useState(false);


    const [pageLoading, setPageLoading] =
        useState(false);


    /*
    |--------------------------------------------------------------------------
    | Handle Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;


        setFormData((previous) => ({

            ...previous,

            [name]: value

        }));
    };


    /*
    |--------------------------------------------------------------------------
    | Load Existing Category
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (!isEditMode) {

            return;

        }


        const loadCategory = async () => {

            try {

                setPageLoading(true);


                const response =
                    await fetch(
                        `${API_BASE_URL}/admin/paper-category/${id}`,
                        {
                            method: "GET",

                            headers: {

                                "Accept":
                                    "application/json"

                            },

                            credentials: "include"

                        }
                    );


                const data =
                    await response.json();


                console.log(
                    "Edit Category Response:",
                    data
                );


                if (!response.ok || !data.success) {

                    throw new Error(
                        data.message ||
                        "Unable to load paper category."
                    );
                }


                const category =
                    data.data;


                setFormData({

                    paper:
                        category.paper ?? "",

                    price:
                        category.price ?? "",

                    duration:
                        category.duration ?? "",

                    link:
                        category.link ?? "",

                    detail1:
                        category.detail1 ?? "",

                    detail2:
                        category.detail2 ?? "",

                    detail3:
                        category.detail3 ?? "",

                    detail4:
                        category.detail4 ?? "",

                    negative_marks:
                        category.negative_marks ?? "0",

                    marks:
                        category.marks ?? "0",

                    show_on_home:
                        category.show_on_home ?? "No",

                    status:
                        category.status ?? "Enable"

                });


            } catch (error) {

                console.error(
                    "Load Category Error:",
                    error
                );


                Swal.fire({

                    icon: "error",

                    title: "Unable to Load",

                    text:
                        error.message ||
                        "Unable to load paper category."

                }).then(() => {

                    navigate(
                        "/paper-category"
                    );

                });


            } finally {

                setPageLoading(false);

            }

        };


        loadCategory();

    }, [
        id,
        isEditMode,
        navigate
    ]);


    /*
    |--------------------------------------------------------------------------
    | Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {

        e.preventDefault();


        if (loading) {

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Validation
        |--------------------------------------------------------------------------
        */

        if (!formData.paper.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Paper Name Required",

                text:
                    "Please enter paper name."

            });

            return;
        }


        if (
            formData.price === "" ||
            Number(formData.price) < 0
        ) {

            Swal.fire({

                icon: "warning",

                title: "Invalid Price",

                text:
                    "Please enter a valid price."

            });

            return;
        }


        if (!formData.duration.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Duration Required",

                text:
                    "Please enter duration."

            });

            return;
        }


        try {

            setLoading(true);


            /*
            |--------------------------------------------------------------------------
            | URL
            |--------------------------------------------------------------------------
            */

            const url = isEditMode

                ? `${API_BASE_URL}/admin/paper-category/${id}`

                : `${API_BASE_URL}/admin/paper-category`;


            /*
            |--------------------------------------------------------------------------
            | Method
            |--------------------------------------------------------------------------
            */

            const method = isEditMode
                ? "PUT"
                : "POST";


            /*
            |--------------------------------------------------------------------------
            | Request
            |--------------------------------------------------------------------------
            */

            const response =
                await fetch(
                    url,
                    {

                        method,

                        headers: {

                            "Accept":
                                "application/json",

                            "Content-Type":
                                "application/json"

                        },

                        credentials: "include",

                        body: JSON.stringify({

                            paper:
                                formData.paper.trim(),

                            price:
                                Number(formData.price),

                            duration:
                                formData.duration.trim(),

                            link:
                                formData.link.trim(),

                            detail1:
                                formData.detail1.trim(),

                            detail2:
                                formData.detail2.trim(),

                            detail3:
                                formData.detail3.trim(),

                            detail4:
                                formData.detail4.trim(),

                            negative_marks:
                                Number(
                                    formData.negative_marks
                                ),

                            marks:
                                formData.marks.trim(),

                            show_on_home:
                                formData.show_on_home,

                            status:
                                formData.status

                        })

                    }
                );


            const data =
                await response.json();


            console.log(
                "Save Category Response:",
                data
            );


            /*
            |--------------------------------------------------------------------------
            | Validation Error
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 422 &&
                data.errors
            ) {

                const errors =
                    Object.values(
                        data.errors
                    ).flat();


                Swal.fire({

                    icon: "error",

                    title: "Validation Error",

                    html:
                        errors
                            .map(
                                (error) =>
                                    `<div>${error}</div>`
                            )
                            .join("")

                });


                return;
            }


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 401 ||
                response.status === 403
            ) {

                Swal.fire({

                    icon: "error",

                    title: "Unauthorized",

                    text:
                        data.message ||
                        "You are not authorized."

                });


                return;
            }


            /*
            |--------------------------------------------------------------------------
            | API Error
            |--------------------------------------------------------------------------
            */

            if (
                !response.ok ||
                !data.success
            ) {

                throw new Error(

                    data.message ||
                    "Unable to save paper category."

                );
            }


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            await Swal.fire({

                icon: "success",

                title:
                    isEditMode
                        ? "Category Updated!"
                        : "Category Added!",

                text:
                    data.message ||
                    (
                        isEditMode
                            ? "Paper category updated successfully."
                            : "Paper category added successfully."
                    ),

                confirmButtonText: "OK",

                confirmButtonColor:
                    "#198754"

            });


            /*
            |--------------------------------------------------------------------------
            | Redirect
            |--------------------------------------------------------------------------
            */

            navigate(
                "/paper-category"
            );


        } catch (error) {

            console.error(
                "Save Category Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    error.message ||
                    "Unable to connect with server."

            });


        } finally {

            setLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Reset
    |--------------------------------------------------------------------------
    */

    const handleReset = () => {

        if (isEditMode) {

            return;

        }


        setFormData({

            paper: "",

            price: "",

            duration: "",

            link: "",

            detail1: "",

            detail2: "",

            detail3: "",

            detail4: "",

            negative_marks: "0",

            marks: "0",

            show_on_home: "No",

            status: "Enable"

        });

    };


    /*
    |--------------------------------------------------------------------------
    | Page Loading
    |--------------------------------------------------------------------------
    */

    if (pageLoading) {

        return (

            <div className="container-fluid">

                <div className="text-center py-5">

                    <div
                        className="spinner-border text-primary"
                        role="status"
                    />

                    <p className="mt-3">
                        Loading paper category...
                    </p>

                </div>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | JSX
    |--------------------------------------------------------------------------
    */

    return (

        <div className="container-fluid py-3" style={{
                            maxHeight: "500px",
                            overflowX: "auto",
                            overflowY: "auto"
                        }}>

            <div className="row justify-content-center">

                <div className="col-xl-10 col-lg-11">


                    <div className="card shadow-sm border-0">


                        {/* HEADER */}

                        <div className="card-header bg-primary text-white py-3">

                            <div className="d-flex align-items-center">

                                <i className="bi bi-pencil-square fs-4 me-2"></i>

                                <div>

                                    <h4 className="mb-0">

                                        {isEditMode
                                            ? "Edit Paper Category"
                                            : "Add Paper Category"}

                                    </h4>

                                    <small>

                                        {isEditMode
                                            ? "Update paper plan details"
                                            : "Create a new paper plan"}

                                    </small>

                                </div>

                            </div>

                        </div>


                        {/* BODY */}

                        <div className="card-body p-0" >


                            <form
                                onSubmit={handleSubmit}
                            >


                                {/* BASIC INFORMATION */}

                                <h5 className="border-bottom pb-2 mb-4">

                                    <i className="bi bi-info-circle me-2"></i>

                                    Basic Information

                                </h5>


                                {/* PAPER */}

                                <div className="mb-3">

                                    <label className="form-label fw-semibold">

                                        Paper Title

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <input
                                        type="text"
                                        className="form-control"
                                        name="paper"
                                        placeholder="Enter paper title"
                                        value={formData.paper}
                                        onChange={handleChange}
                                        maxLength="250"
                                    />

                                </div>


                                {/* PRICE + DURATION */}

                                <div className="row">


                                    <div className="col-md-6 mb-3">

                                        <label className="form-label fw-semibold">

                                            Price (₹)

                                            <span className="text-danger">
                                                *
                                            </span>

                                        </label>


                                        <input
                                            type="number"
                                            className="form-control"
                                            name="price"
                                            min="0"
                                            step="1"
                                            placeholder="Enter price"
                                            value={formData.price}
                                            onChange={handleChange}
                                        />

                                    </div>


                                    <div className="col-md-6 mb-3">

                                        <label className="form-label fw-semibold">

                                            Duration

                                            <span className="text-danger">
                                                *
                                            </span>

                                        </label>


                                        <input
                                            type="text"
                                            className="form-control"
                                            name="duration"
                                            placeholder="Example: 30 Days"
                                            value={formData.duration}
                                            onChange={handleChange}
                                        />

                                    </div>

                                </div>


                                {/* LINK */}

                                <div className="mb-4">

                                    <label className="form-label fw-semibold">

                                        Purchase Link

                                    </label>


                                    <input
                                        type="text"
                                        className="form-control"
                                        name="link"
                                        placeholder="https://example.com/payment"
                                        value={formData.link}
                                        onChange={handleChange}
                                    />

                                </div>


                                {/* DETAILS */}

                                <h5 className="border-bottom pb-2 mb-4">

                                    <i className="bi bi-list-check me-2"></i>

                                    Plan Details

                                </h5>


                                <div className="row">


                                    <div className="col-md-6 mb-3">

                                        <label className="form-label fw-semibold">
                                            Detail 1
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            name="detail1"
                                            placeholder="Enter detail"
                                            value={formData.detail1}
                                            onChange={handleChange}
                                        />

                                    </div>


                                    <div className="col-md-6 mb-3">

                                        <label className="form-label fw-semibold">
                                            Detail 2
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            name="detail2"
                                            placeholder="Enter detail"
                                            value={formData.detail2}
                                            onChange={handleChange}
                                        />

                                    </div>


                                    <div className="col-md-6 mb-3">

                                        <label className="form-label fw-semibold">
                                            Detail 3
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            name="detail3"
                                            placeholder="Enter detail"
                                            value={formData.detail3}
                                            onChange={handleChange}
                                        />

                                    </div>


                                    <div className="col-md-6 mb-3">

                                        <label className="form-label fw-semibold">
                                            Detail 4
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            name="detail4"
                                            placeholder="Enter detail"
                                            value={formData.detail4}
                                            onChange={handleChange}
                                        />

                                    </div>

                                </div>


                                {/* MARKING */}

                                <h5 className="border-bottom pb-2 mb-4 mt-3">

                                    <i className="bi bi-calculator me-2"></i>

                                    Marking

                                </h5>


                                <div className="row">


                                    <div className="col-md-6 mb-3">

                                        <label className="form-label fw-semibold">

                                            Negative Marks

                                        </label>


                                        <input
                                            type="number"
                                            className="form-control"
                                            name="negative_marks"
                                            min="0"
                                            step="0.01"
                                            value={formData.negative_marks}
                                            onChange={handleChange}
                                        />

                                    </div>


                                    <div className="col-md-6 mb-3">

                                        <label className="form-label fw-semibold">

                                            Marks Per Question

                                        </label>


                                        <input
                                            type="text"
                                            className="form-control"
                                            name="marks"
                                            placeholder="Example: 1"
                                            value={formData.marks}
                                            onChange={handleChange}
                                        />

                                    </div>

                                </div>


                                {/* SETTINGS */}

                                <h5 className="border-bottom pb-2 mb-4 mt-3">

                                    <i className="bi bi-gear me-2"></i>

                                    Settings

                                </h5>


                                <div className="row">


                                    {/* SHOW ON HOME */}

                                    <div className="col-md-6 mb-4">

                                        <label className="form-label fw-semibold">

                                            Show On Home

                                        </label>


                                        <select
                                            className="form-select"
                                            name="show_on_home"
                                            value={formData.show_on_home}
                                            onChange={handleChange}
                                        >

                                            <option value="No">
                                                No
                                            </option>

                                            <option value="Yes">
                                                Yes
                                            </option>

                                        </select>

                                    </div>


                                    {/* STATUS */}

                                    <div className="col-md-6 mb-4">

                                        <label className="form-label fw-semibold">

                                            Status

                                        </label>


                                        <select
                                            className="form-select"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                        >

                                            <option value="Enable">
                                                Enable
                                            </option>

                                            <option value="Disable">
                                                Disable
                                            </option>

                                        </select>

                                    </div>

                                </div>


                                {/* BUTTONS */}

                                <div className="d-flex gap-2 border-top pt-4">


                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={loading}
                                    >

                                        {loading ? (

                                            <>

                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                />

                                                {isEditMode
                                                    ? "Updating..."
                                                    : "Saving..."}

                                            </>

                                        ) : (

                                            <>

                                                <i
                                                    className={
                                                        isEditMode
                                                            ? "bi bi-check-lg me-2"
                                                            : "bi bi-plus-lg me-2"
                                                    }
                                                />

                                                {isEditMode
                                                    ? "Update Category"
                                                    : "Save Category"}

                                            </>

                                        )}

                                    </button>


                                    {!isEditMode && (

                                        <button
                                            type="button"
                                            className="btn btn-secondary px-4"
                                            onClick={handleReset}
                                            disabled={loading}
                                        >

                                            <i className="bi bi-arrow-counterclockwise me-2"></i>

                                            Reset

                                        </button>

                                    )}


                                    <button
                                        type="button"
                                        className="btn btn-outline-dark px-4"
                                        onClick={() =>
                                            navigate(
                                                "/paper-category"
                                            )
                                        }
                                        disabled={loading}
                                    >

                                        <i className="bi bi-arrow-left me-2"></i>

                                        Back

                                    </button>


                                </div>


                            </form>

                        </div>

                    </div>

                </div>

            </div>

        </div>

    );
}


export default AddCategory;


